/* Original Evolved protection controller. Shopify owns prices, discounts and totals. */
(() => {
  'use strict';
  if (window.EvolvedShippingProtection) return;
  const node = document.getElementById('evolved-protection-config');
  if (!node) return;
  let config;
  try { config = JSON.parse(node.textContent); } catch (_) { return; }
  const strings = config.strings || {};
  const tiers = (config.variants || []).filter(v => Number.isSafeInteger(v.id) && Number.isInteger(v.price) && v.price > 0).sort((a, b) => a.price - b.price);
  if (!tiers.length || !(config.rate > 0 && config.rate <= 5)) return;

  const attribute = 'ev_shipping_protection';
  const protectionIds = [config.productId, config.legacyProductId].filter(Boolean).map(String);
  const isProtection = item => protectionIds.includes(String(item.product_id));
  const netLine = item => Math.max(0, Math.min(item.final_line_price, item.original_line_price - (item.total_discount || 0)));
  const eligible = item => !isProtection(item) && item.requires_shipping === true && !item.gift_card;
  const feeLines = cart => cart.items.filter(isProtection);
  const tierPriceMismatch = current => feeLines(current).some(line => {
    const tier = tiers.find(value => String(value.id) === String(line.variant_id));
    return tier && Math.abs(line.original_line_price - tier.price * line.quantity) > 1;
  });
  const itemCount = cart => cart.items.reduce((sum, item) => sum + (isProtection(item) ? 0 : item.quantity), 0);
  const quote = cart => {
    if (cart.currency !== config.currency || !Number.isFinite(cart.total_price)) throw new Error('currency');
    if (!cart.items.some(eligible)) return null;
    const excluded = cart.items.filter(item => !eligible(item)).reduce((sum, item) => sum + netLine(item), 0);
    const basis = Math.max(0, cart.total_price - excluded);
    if (!Number.isFinite(basis) || basis <= 0) return null;
    // Match the existing min/max and round UP to a real Shopify price tier.
    // Presentment prices come from Liquid; no exchange-rate guesses or fake fees.
    const target = Math.ceil(basis * config.rate / 100 - 1e-8);
    return tiers.find(tier => tier.price >= target) || tiers[tiers.length - 1];
  };
  // Country selection is a storefront market, not proof of a shipping address.
  // The merchant can select all other markets (*) or individual country codes.
  // EU/EEA and UK paid extras always require an affirmative customer choice.
  const expressConsentCountries = new Set('AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL PL PT RO SK SI ES SE AX GF GP MQ MF RE YT IS LI NO GB'.split(' '));
  const autoSelected = /^[A-Z]{2}$/.test(config.country || '') && !expressConsentCountries.has(config.country) && (config.autoCountries || []).some(value => value.trim() === '*' || value.trim() === config.country);
  const selected = cart => {
    const preference = cart.attributes?.[attribute];
    if (preference === 'yes') return true;
    if (preference === 'no') return false;
    return autoSelected;
  };
  const plan = (cart, choice) => {
    const requested = choice === undefined ? selected(cart) : choice;
    const tier = requested ? quote(cart) : null, lines = feeLines(cart);
    const wants = Boolean(tier && requested);
    const updates = {};
    let kept = false;
    for (const line of lines) {
      if (wants && String(line.variant_id) === String(tier.id) && !kept) {
        kept = true;
        if (line.quantity !== 1) updates[line.key] = 1;
      } else updates[line.key] = 0;
    }
    if (wants && !kept) updates[tier.id] = 1;
    const previous = cart.attributes?.[attribute];
    const preference = choice === false ? 'no' : !cart.items.some(eligible) ? '' : choice !== undefined ? (wants ? 'yes' : 'no') : ['yes', 'no'].includes(previous) ? previous : wants ? 'auto:' + config.country : '';
    return { updates, preference, changed: Object.keys(updates).length > 0 || (cart.attributes?.[attribute] || '') !== preference };
  };
  const cartUrl = path => {
    const url = new URL((window.Shopify?.routes?.root || '/') + path, location.origin);
    // Shopify's localization form owns market/currency cookies. Use the same
    // locale-aware routes as native ATC and checkout, without market overrides.
    return url.pathname + url.search;
  };
  const rawRequest = async (path, body) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
    const response = await fetch(cartUrl(path), {
      signal: controller.signal,
      method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'request');
    return data;
    } finally { clearTimeout(timeout); }
  };
  const request = async (path, body) => {
    const data = await rawRequest(path, body);
    return path.startsWith('cart/') && path !== 'cart/add.js' && body?.discount === undefined && Array.isArray(data.items) && window.EvolvedCartDiscounts
      ? window.EvolvedCartDiscounts.restore(data, rawRequest) : data;
  };
  const sections = () => {
    const ids = [];
    const pageSection = document.querySelector('.ev-cart-page__summary')?.closest('.shopify-section');
    if (pageSection) ids.push(pageSection.id.replace('shopify-section-', ''));
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: ids } }));
    return [...new Set(ids)].slice(0, 5);
  };
  let cart = config.cart, work = Promise.resolve(), pending = 0, timer, ownEvent = false, error = '', notice = '', pendingChoice, pricingReady = false;
  const paused = new Set();
  const mounts = new Set();
  const money = value => new Intl.NumberFormat(config.locale, { style: 'currency', currency: config.currency }).format(value / 100);
  const paint = () => {
    let tier;
    try { tier = quote(cart); } catch (_) { tier = null; }
    const lines = feeLines(cart), actual = lines.reduce((sum, line) => sum + netLine(line), 0);
    const checked = lines.length > 0;
    const actualTier = lines.length === 1 && lines[0].quantity === 1 ? tiers.find(v => String(v.id) === String(lines[0].variant_id) && v.price === actual) : null;
    const price = checked ? actualTier?.formatted || money(actual) : tier?.formatted || (tier ? money(tier.price) : '');
    for (const mount of mounts) {
      mount.hidden = !tier && !checked && !error;
      mount.toggleAttribute('aria-busy', pending > 0 || paused.size > 0);
      if (pending || paused.size) mount.setAttribute('aria-busy', 'true');
      mount.dataset.state = checked ? 'on' : 'off';
      mount.querySelector('[data-ev-protection-offer]').hidden = checked;
      const busy = pending > 0 || paused.size > 0;
      const adding = pending > 0 && pendingChoice === true;
      const button = mount.querySelector('[data-ev-protection-add]');
      button.disabled = busy || checked || !tier;
      button.setAttribute('aria-label', (strings.add || 'Add Shipping Protection') + ' — ' + price);
      mount.querySelector('[data-ev-protection-action]').textContent = adding ? strings.adding || 'Adding…' : strings.add_short || 'Add';
      const dialogButton = mount.querySelector('[data-ev-protection-dialog-add]');
      dialogButton.disabled = busy || checked || !tier;
      dialogButton.textContent = pending > 0 && pendingChoice !== undefined ? strings.updating || 'Updating…' : checked ? strings.included || 'Included in your cart' : (strings.add || 'Add Shipping Protection') + ' — ' + price;
      mount.querySelector('[data-ev-protection-price]').textContent = price;
      mount.querySelector('[data-ev-protection-error]').textContent = error;
      mount.querySelector('[data-ev-protection-status]').textContent = notice;
    }
    for (const remove of document.querySelectorAll('[data-ev-protection-remove]')) {
      if (pending || paused.size) remove.setAttribute('aria-disabled', 'true');
      else remove.removeAttribute('aria-disabled');
    }
  };
  const queue = fn => {
    pending++; paint();
    const next = work.then(fn, fn).finally(() => { pending--; paint(); });
    work = next.catch(() => {});
    return next;
  };
  const nativeBusy = () => document.querySelector('line-item-quantity[aria-busy="true"], product-form [aria-busy="true"], [data-ev-add][aria-busy="true"]');
  const waitForNative = async () => {
    const deadline = Date.now() + 12000;
    while (nativeBusy()) {
      if (Date.now() > deadline) throw new Error('busy');
      await new Promise(resolve => setTimeout(resolve, 80));
    }
  };
  const refresh = async current => {
    const ids = sections();
    if (!ids.every(id => typeof current.sections?.[id] === 'string')) {
      current.sections = await request('?' + new URLSearchParams({ sections: ids.join(',') }));
    }
    if (ids.length && ids.every(id => typeof current.sections?.[id] === 'string')) {
      // The cart and the product quote must come from the same market request.
      // Refresh the real variant ladder; do not estimate an exchange rate.
      for (const html of Object.values(current.sections)) {
        const pricesNode = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ev-protection-prices]');
        if (!pricesNode) continue;
        let prices;
        try { prices = JSON.parse(pricesNode.textContent); } catch (_) { continue; }
        const fresh = (prices.variants || []).filter(v => Number.isSafeInteger(v.id) && Number.isInteger(v.price) && v.price > 0).sort((a, b) => a.price - b.price);
        if (fresh.length && prices.country === config.country && prices.currency === current.currency && current.currency === (config.marketCurrency || config.currency)) {
          tiers.splice(0, tiers.length, ...fresh); config.currency = current.currency; pricingReady = true; break;
        }
      }
      const pageSection = document.querySelector('.ev-cart-page__summary')?.closest('.shopify-section');
      if (pageSection) {
        const pageHtml = current.sections[pageSection.id.replace('shopify-section-', '')];
        const replacement = new DOMParser().parseFromString(pageHtml, 'text/html').getElementById(pageSection.id);
        if (!replacement) throw new Error('render');
        pageSection.replaceChildren(...replacement.childNodes);
      }
      ownEvent = true;
      try { document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'evolved:protection', cart: current } })); }
      finally { ownEvent = false; }
    } else throw new Error('render');
  };
  const reconcile = async (choice, render = true, directChoice = false) => {
    await waitForNative();
    let changed = false;
    if (directChoice && choice === true && !pricingReady) {
      cart = await request('cart.js');
      await refresh(cart);
      if (!pricingReady || cart.currency !== config.currency) throw new Error('currency');
    }
    const verifyTierPrice = async () => {
      if (!tierPriceMismatch(cart)) return;
      // A country/tax-context change can leave the same currency code but a
      // different server price. Do not retain a fee above the displayed quote.
      cart = await request('cart/update.js', { updates: Object.fromEntries(feeLines(cart).map(line => [line.key, 0])), attributes: { [attribute]: 'no' }, sections: sections(), sections_url: location.pathname });
      throw new Error('currency');
    };
    if (directChoice) {
      // A direct choice needs only one write: update fee keys and preference,
      // then reconcile against Shopify's fresh response. No merchandise writes.
      const next = plan(cart, choice);
      cart = await request('cart/update.js', { updates: next.updates, attributes: { [attribute]: next.preference }, sections: sections(), sections_url: location.pathname });
      changed = true;
      if (choice && cart.currency !== config.currency) {
        // A stale market quote must never leave its newly added fee behind.
        cart = await request('cart/update.js', { updates: Object.fromEntries(feeLines(cart).map(line => [line.key, 0])), attributes: { [attribute]: 'no' }, sections: sections(), sections_url: location.pathname });
        throw new Error('currency');
      }
    } else cart = await request('cart.js');
    if ((!pricingReady || cart.currency !== config.currency) && (choice === undefined ? selected(cart) : choice)) {
      await refresh(cart);
      if (!pricingReady || cart.currency !== config.currency) throw new Error('currency');
    } else if (!pricingReady && render && !directChoice) {
      await refresh(cart);
    }
    await verifyTierPrice();
    for (let attempt = 0; attempt < 3; attempt++) {
      const next = plan(cart, choice);
      if (!next.changed) {
        error = ''; paint();
        if (changed && render) await refresh(cart);
        return cart;
      }
      // One native cart update changes only protection quantities and our own
      // preference. Merchandise quantities, properties and discount codes survive.
      cart = await request('cart/update.js', {
        updates: next.updates, attributes: { [attribute]: next.preference },
        sections: sections(), sections_url: location.pathname
      });
      await verifyTierPrice();
      changed = true;
    }
    if (plan(cart, choice).changed) throw new Error('changed');
    error = ''; paint();
    if (changed && render) await refresh(cart);
    return cart;
  };
  const showError = async failure => {
    try { cart = await request('cart.js'); await refresh(cart); } catch (_) {}
    error = failure.message === 'currency' ? strings.price_error || 'Protection pricing changed. You can continue without this add-on.' : strings.error || 'Protection could not be updated. Please try again, or remove it from your cart.';
    paint();
  };
  const schedule = () => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      if (pending || paused.size || nativeBusy()) { schedule(); return; }
      if (error) return;
      let needed;
      try { needed = (!pricingReady && cart.items.some(eligible)) || tierPriceMismatch(cart) || plan(cart).changed; } catch (_) { paint(); return; }
      if (needed) queue(() => reconcile()).catch(showError);
    }, 180);
  };
  const choose = (value, mount, trigger) => {
    if (pending || paused.size) return;
    const context = mount.closest('cart-drawer') ? 'cart-drawer' : 'main';
    const restoreKeyboardFocus = trigger ? trigger.matches(':focus-visible') : mount.dataset.inputMethod === 'keyboard';
    clearTimeout(timer);
    pendingChoice = value;
    notice = '';
    queue(() => reconcile(value, true, true)).then(() => {
      notice = value ? strings.added_status || 'Shipping Protection added to your cart.' : strings.removed_status || 'Shipping Protection removed from your cart.';
      if (restoreKeyboardFocus) document.querySelector(context + (value ? ' [data-ev-protection-line-details]' : ' [data-ev-protection-add]'))?.focus({ preventScroll: true });
    }).catch(showError).finally(() => { pendingChoice = undefined; paint(); });
  };

  window.EvolvedShippingProtection = {
    itemCount,
    context() { return { country: config.country, currency: config.marketCurrency || config.currency }; },
    pause(key) { paused.add(key); clearTimeout(timer); paint(); return work; },
    resume(key) { paused.delete(key); paint(); schedule(); }
  };
  class EvolvedShippingProtection extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      if (!pending) {
        try { cart = JSON.parse(this.querySelector('[data-ev-protection-cart]').textContent); } catch (_) {}
      }
      mounts.add(this);
      this.addEventListener('pointerdown', () => { this.dataset.inputMethod = 'pointer'; }, { signal: this.abort.signal });
      this.addEventListener('keydown', () => { this.dataset.inputMethod = 'keyboard'; }, { signal: this.abort.signal });
      const signal = this.abort.signal;
      this.dialog = this.querySelector('dialog');
      this.querySelector('[data-ev-protection-add]').addEventListener('click', event => choose(true, this, event.currentTarget), { signal });
      this.querySelector('[data-ev-protection-dialog-add]').addEventListener('click', event => choose(true, this, event.currentTarget), { signal });
      this.querySelector('[data-ev-protection-details]').addEventListener('click', event => { event.preventDefault(); event.stopPropagation(); this.open(event.currentTarget); }, { signal });
      this.querySelector('[data-ev-protection-close]').addEventListener('click', () => this.close(), { signal });
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, { signal });
      this.dialog.addEventListener('click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
      }, { signal });
      paint(); schedule();
    }
    open(trigger) {
      this.returnFocus = trigger || document.activeElement;
      paint();
      if (!this.dialog.open) this.dialog.showModal();
    }
    close() {
      if (!this.dialog?.open) return;
      this.dialog.close();
      if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll:true });
      this.returnFocus = null;
    }
    disconnectedCallback() { this.close(); this.abort?.abort(); this.abort = null; mounts.delete(this); }
  }
  customElements.define('evolved-shipping-protection', EvolvedShippingProtection);
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-ev-protection-line-details], [data-ev-protection-remove]');
    if (!trigger) return;
    const scope = trigger.closest('#cart-drawer, .shopify-section--main-cart');
    const mount = scope?.querySelector('evolved-shipping-protection');
    if (!mount) return;
    event.preventDefault();
    if (trigger.hasAttribute('data-ev-protection-remove')) {
      event.stopImmediatePropagation();
      choose(false, mount, trigger);
    } else mount.open(trigger);
  }, true);
  document.addEventListener('keydown', event => {
    const mount = [...mounts].find(value => value.dialog?.open);
    if (!mount) return;
    if (event.key === 'Escape') { event.preventDefault(); event.stopImmediatePropagation(); mount.close(); }
    else if (event.key === 'Tab') {
      event.stopImmediatePropagation();
      const focusable = [...mount.dialog.querySelectorAll('button:not(:disabled), a[href]')].filter(element => element.getClientRects().length && !element.hidden);
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
  }, true);
  document.addEventListener('cart:change', event => {
    if (ownEvent || !event.detail?.cart) return;
    cart = event.detail.cart;
    error = ''; paint(); schedule();
  });
  document.addEventListener('cart:refresh', () => {
    if (pending || ownEvent) return;
    queue(async () => { cart = await request('cart.js'); paint(); }).then(schedule).catch(showError);
  });
  window.addEventListener('pageshow', event => {
    if (event.persisted) queue(() => reconcile()).catch(showError);
  });

  // Reconcile selected protection before the normal cart checkout submission.
  // This does not place an order or inject a checkout widget.
  let resubmitting = false, checkingOut = false;
  document.addEventListener('submit', event => {
    const form = event.target, submitter = event.submitter;
    if (resubmitting || !(form instanceof HTMLFormElement) || submitter?.name !== 'checkout') return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (checkingOut) return;
    checkingOut = true; clearTimeout(timer);
    const context = form.closest('cart-drawer') ? 'cart-drawer' : 'main';
    queue(async () => {
      try { return await reconcile(undefined, false); }
      catch (failure) {
        if (failure.message !== 'currency') throw failure;
        const current = await request('cart.js');
        // A failed automatic quote must not trap a cart that has no fee.
        // Continue only after Shopify confirms no protection is being charged.
        if (feeLines(current).length) throw failure;
        return current;
      }
    }).then(current => {
      const button = submitter.isConnected ? submitter : document.querySelector(context + ' [name="checkout"]');
      if (!button?.form) throw new Error('changed');
      const activeCodes = (current.discount_codes || []).filter(discount => discount.applicable !== false).map(discount => discount.code);
      for (const item of current.items) for (const allocation of item.line_level_discount_allocations || []) {
        if (allocation.discount_application?.type === 'discount_code') activeCodes.push(allocation.discount_application.title);
      }
      for (const discount of current.cart_level_discount_applications || []) if (discount.type === 'discount_code') activeCodes.push(discount.title);
      const retained = [...new Set(activeCodes.filter(Boolean))];
      // Carry confirmed codes through the native form as well as the cart cookie.
      // A previous checkout session or an empty discount field can drop the cookie.
      if (retained.length) {
        let field = button.form.querySelector('[name="discount"]');
        if (!field) { field = document.createElement('input'); field.type = 'hidden'; field.name = 'discount'; button.form.append(field); }
        field.dataset.evCartDiscounts = '1';
        field.value = retained.join(',');
      } else button.form.querySelector('[data-ev-cart-discounts]')?.remove();
      resubmitting = true;
      try { button.form.requestSubmit(button); } finally { resubmitting = false; }
    }).catch(showError).finally(() => { checkingOut = false; });
  }, true);
  customElements.whenDefined('cart-count').then(() => {
    document.querySelectorAll('cart-count').forEach(count => { count.itemCount = itemCount(cart); });
  });
  schedule();
})();

